import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  SHARE_REFRESH_INTERVALS,
  SHARE_VALIDITY_LABELS,
  MIN_SHARE_PASSWORD_LENGTH,
  type CreateShareLinkInput,
  type DashboardShareEvent,
  type ShareAccess,
  type ShareRefreshMode,
  type ShareScopeMode,
  type ShareValidity
} from "../../domain/dashboard/dashboard-share.js";
import type { DashboardShareLinkView } from "../../application/services/dashboard-share-service.js";
import type { DashboardFilters } from "../../domain/dashboard/dashboard-indicators.js";

interface Opt { id: string; label: string; projectId?: string }

export interface SharePanelInit {
  filters: DashboardFilters;
  projects: Opt[];
  unitAreas: Opt[];
  inspectors: Opt[];
}

interface FormState {
  name: string;
  description: string;
  validity: ShareValidity;
  customExpiresAt: string;
  projectId: string;
  unitAreaId: string;
  inspectorProfessionalId: string;
  from: string;
  to: string;
  mode: ShareScopeMode;
  showInspectors: boolean;
  refreshMode: ShareRefreshMode;
  refreshIntervalSeconds: number;
  access: ShareAccess;
  password: string;
}

const EVENT_LABELS: Record<string, string> = {
  CREATED: "Link criado", UPDATED: "Link alterado", RENEWED: "Link renovado", REVOKED: "Link revogado", ACCESSED: "Painel aberto pelo link"
};

/**
 * COMPARTILHAR PAINEL DE INDICADORES E AVANÇOS — modal de criação e gestão
 * dos links. A tela só desenha: permissão, escopo, validade e senha são
 * conferidos de novo no servidor (`DashboardShareService`).
 */
class DashboardSharePanel extends HTMLElement {
  private init: SharePanelInit = { filters: {}, projects: [], unitAreas: [], inspectors: [] };
  private tab: "new" | "list" = "new";
  private form!: FormState;
  private links: DashboardShareLinkView[] = [];
  private listLoaded = false;
  private busy = false;
  private error = "";
  private notice = "";
  private created: DashboardShareLinkView | null = null;
  private renewing: string | null = null;
  private revoking: string | null = null;
  private history: { id: string; events: DashboardShareEvent[] } | null = null;
  private readonly onKey = (e: KeyboardEvent) => { if (e.key === "Escape") this.close(); };

  open(init: SharePanelInit): void {
    this.init = init;
    this.form = this.formFromFilters(init.filters);
    this.tab = this.can(PermissionCode.DashboardShareCreate) ? "new" : "list";
    document.addEventListener("keydown", this.onKey);
    this.render();
    if (this.tab === "list") void this.loadLinks();
  }

  disconnectedCallback(): void {
    document.removeEventListener("keydown", this.onKey);
  }

  private close(): void {
    this.remove();
  }

  private can(code: string): boolean {
    return contextStore.hasPermission(code as any);
  }

  private get svc(): any {
    return (window as any).app?.platform?.dashboardShareService;
  }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private formFromFilters(f: DashboardFilters): FormState {
    return {
      name: "", description: "", validity: "7D", customExpiresAt: "",
      projectId: f.projectId || "", unitAreaId: f.unitAreaId || "", inspectorProfessionalId: f.inspectorProfessionalId || "",
      from: f.from || "", to: f.to || "",
      mode: "FIXED", showInspectors: false,
      refreshMode: "REALTIME", refreshIntervalSeconds: 30,
      access: "PUBLIC", password: ""
    };
  }

  private messageOf(e: unknown): string {
    const err = e as { safeMessage?: string; message?: string };
    return err?.safeMessage || err?.message || "Não foi possível concluir a operação.";
  }

  private async run(action: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    this.notice = "";
    this.render();
    try {
      await action();
    } catch (e) {
      this.error = this.messageOf(e);
    } finally {
      this.busy = false;
      this.render();
    }
  }

  private async loadLinks(): Promise<void> {
    await this.run(async () => {
      this.links = await this.svc.listLinks(this.ctx());
      this.listLoaded = true;
    });
  }

  private fullUrl(path: string): string {
    return `${window.location.origin}${path}`;
  }

  private async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.notice = "Link copiado.";
    } catch {
      const input = this.querySelector<HTMLInputElement>("#dsp-link-value");
      if (input) { input.select(); document.execCommand("copy"); this.notice = "Link copiado."; } else this.notice = "Copie o link manualmente.";
    }
    this.render();
  }

  // ─────────────────────────────────────────────────────────── ações

  private async generate(): Promise<void> {
    const f = this.form;
    const input: CreateShareLinkInput = {
      name: f.name, description: f.description || null,
      validity: f.validity, customExpiresAt: f.validity === "CUSTOM" && f.customExpiresAt ? new Date(f.customExpiresAt).toISOString() : null,
      scope: {
        projectId: f.projectId || null, unitAreaId: f.unitAreaId || null, inspectorProfessionalId: f.inspectorProfessionalId || null,
        from: f.from || null, to: f.to || null, mode: f.mode, showInspectors: f.showInspectors
      },
      refreshMode: f.refreshMode, refreshIntervalSeconds: f.refreshIntervalSeconds,
      access: f.access, password: f.access === "PASSWORD" ? f.password : null
    };
    await this.run(async () => {
      this.created = await this.svc.createLink(this.ctx(), input);
      this.form.password = "";
      this.listLoaded = false;
    });
  }

  private async revoke(id: string, reason: string): Promise<void> {
    await this.run(async () => {
      const view: DashboardShareLinkView = await this.svc.revokeLink(this.ctx(), id, reason || null);
      if (this.created?.id === id) this.created = view;
      this.links = this.links.map(l => (l.id === id ? view : l));
      this.revoking = null;
      this.notice = "Link revogado. Ele não abre mais.";
    });
  }

  private async renew(id: string, validity: ShareValidity, custom: string, rotate: boolean): Promise<void> {
    await this.run(async () => {
      const view: DashboardShareLinkView = await this.svc.renewLink(this.ctx(), id, {
        validity, customExpiresAt: validity === "CUSTOM" && custom ? new Date(custom).toISOString() : null, rotateToken: rotate
      });
      this.links = this.links.map(l => (l.id === id ? view : l));
      this.renewing = null;
      this.notice = rotate ? "Link renovado com endereço NOVO — o anterior parou de abrir." : "Validade renovada.";
    });
  }

  private async showHistory(id: string): Promise<void> {
    if (this.history?.id === id) { this.history = null; this.render(); return; }
    await this.run(async () => {
      this.history = { id, events: await this.svc.listLinkEvents(this.ctx(), id) };
    });
  }

  // ─────────────────────────────────────────────────────────── render

  private esc(v: unknown): string {
    return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private when(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  private render(): void {
    const canCreate = this.can(PermissionCode.DashboardShareCreate);
    const canList = this.can(PermissionCode.DashboardShareRead) || this.can(PermissionCode.DashboardShareManage);
    this.innerHTML = `<style>${STYLES}</style>
      <div class="dsp-backdrop" data-close></div>
      <section class="dsp-modal" role="dialog" aria-modal="true" aria-labelledby="dsp-title">
        <header class="dsp-head">
          <h2 id="dsp-title">Compartilhar Painel de Indicadores e Avanços</h2>
          <button class="dsp-icon" type="button" data-close aria-label="Fechar">✕</button>
        </header>
        <nav class="dsp-tabs" role="tablist">
          ${canCreate ? `<button role="tab" type="button" class="${this.tab === "new" ? "on" : ""}" data-tab="new" aria-selected="${this.tab === "new"}">Novo link</button>` : ""}
          ${canList ? `<button role="tab" type="button" class="${this.tab === "list" ? "on" : ""}" data-tab="list" aria-selected="${this.tab === "list"}">Links gerados</button>` : ""}
        </nav>
        <div class="dsp-body">
          ${this.error ? `<div class="dsp-alert err" role="alert">${this.esc(this.error)}</div>` : ""}
          ${this.notice ? `<div class="dsp-alert ok" role="status">${this.esc(this.notice)}</div>` : ""}
          ${this.tab === "new" ? (this.created ? this.renderCreated(this.created) : this.renderForm()) : this.renderList()}
        </div>
      </section>`;
    this.bind();
  }

  private options(list: Opt[], value: string, empty: string): string {
    return `<option value="">${this.esc(empty)}</option>` + list.map(o => `<option value="${this.esc(o.id)}" ${o.id === value ? "selected" : ""}>${this.esc(o.label)}</option>`).join("");
  }

  private renderForm(): string {
    const f = this.form;
    const units = f.projectId ? this.init.unitAreas.filter(u => u.projectId === f.projectId) : this.init.unitAreas;
    const canPeople = this.can(PermissionCode.IdentityRead);
    // O Cliente do link sai do PROJETO escolhido (o servidor resolve e confere
    // o acesso). Só quando não há Projeto nem Cliente ativo é que falta de onde
    // tirar o Cliente — aí o Projeto é o que se pede.
    const needsProject = !contextStore.get().activeClientId && !f.projectId;
    return `
      <form class="dsp-form" id="dsp-form" novalidate>
        <div class="dsp-grid">
          <label class="dsp-field wide"><span>Nome do link *</span><input id="dsp-name" maxlength="120" value="${this.esc(f.name)}" placeholder="Ex.: Avanço U-33 para o Cliente" required></label>
          <label class="dsp-field wide"><span>Descrição (opcional)</span><textarea id="dsp-desc" maxlength="500" rows="2">${this.esc(f.description)}</textarea></label>
          <label class="dsp-field"><span>Validade</span>
            <select id="dsp-validity">${(Object.keys(SHARE_VALIDITY_LABELS) as ShareValidity[]).map(v => `<option value="${v}" ${f.validity === v ? "selected" : ""}>${SHARE_VALIDITY_LABELS[v]}</option>`).join("")}</select>
          </label>
          ${f.validity === "CUSTOM" ? `<label class="dsp-field"><span>Expira em</span><input type="datetime-local" id="dsp-custom" value="${this.esc(f.customExpiresAt)}"></label>` : `<div></div>`}
        </div>

        <fieldset class="dsp-set">
          <legend>Escopo dos dados</legend>
          <p class="dsp-note">Começa com os filtros aplicados no Painel. O Projeto escolhido define o Cliente dos dados; em branco, "todos os projetos" do Cliente selecionado no topo. O terceiro nunca vê nada fora deste recorte.</p>
          ${needsProject ? `<p class="dsp-note dsp-need" role="status">Escolha o Projeto — ele identifica o Cliente dos dados compartilhados.</p>` : ""}
          <div class="dsp-inline">
            <button type="button" class="dsp-btn sm ghost" id="dsp-use-filters">Usar filtros atuais</button>
            <button type="button" class="dsp-btn sm ghost" id="dsp-all">Todos os projetos permitidos</button>
          </div>
          <div class="dsp-grid">
            <label class="dsp-field"><span>Projeto</span><select id="dsp-project">${this.options(this.init.projects, f.projectId, "Todos os Projetos")}</select></label>
            <label class="dsp-field"><span>Unidade / Área</span><select id="dsp-unit">${this.options(units, f.unitAreaId, "Todas as Unidades / Áreas")}</select></label>
            <label class="dsp-field"><span>Inspetor</span><select id="dsp-inspector">${this.options(this.init.inspectors, f.inspectorProfessionalId, "Todos os Inspetores")}</select></label>
            <label class="dsp-field"><span>Período</span><span class="dsp-period"><input type="date" id="dsp-from" value="${this.esc(f.from)}" aria-label="Data inicial"><span>→</span><input type="date" id="dsp-to" value="${this.esc(f.to)}" aria-label="Data final"></span></label>
          </div>
          <div class="dsp-radios">
            <label><input type="radio" name="dsp-mode" value="FIXED" ${f.mode === "FIXED" ? "checked" : ""}> <b>Escopo fixo</b> — o terceiro vê exatamente este recorte.</label>
            <label><input type="radio" name="dsp-mode" value="DYNAMIC" ${f.mode === "DYNAMIC" ? "checked" : ""}> <b>Escopo dinâmico</b> — o terceiro pode filtrar, sempre dentro deste recorte.</label>
          </div>
          <label class="dsp-check"><input type="checkbox" id="dsp-show-inspectors" ${f.showInspectors ? "checked" : ""} ${canPeople ? "" : "disabled"}>
            Mostrar o avanço por Inspetor <small>(exibe o NOME de cada pessoa e o ritmo de trabalho dela a quem tiver o link)</small></label>
          ${canPeople ? "" : `<p class="dsp-note">Seu perfil não lê o cadastro de Profissionais: o ranking por Inspetor não pode ser compartilhado.</p>`}
        </fieldset>

        <fieldset class="dsp-set">
          <legend>Atualização</legend>
          <div class="dsp-radios">
            <label><input type="radio" name="dsp-refresh" value="REALTIME" ${f.refreshMode === "REALTIME" ? "checked" : ""}> Em tempo real (a cada 15 segundos)</label>
            <label><input type="radio" name="dsp-refresh" value="INTERVAL" ${f.refreshMode === "INTERVAL" ? "checked" : ""}> Automática a cada
              <select id="dsp-interval" ${f.refreshMode === "INTERVAL" ? "" : "disabled"}>${SHARE_REFRESH_INTERVALS.map(s => `<option value="${s}" ${f.refreshIntervalSeconds === s ? "selected" : ""}>${s} s</option>`).join("")}</select></label>
            <label><input type="radio" name="dsp-refresh" value="MANUAL" ${f.refreshMode === "MANUAL" ? "checked" : ""}> Manual (o terceiro clica em Atualizar)</label>
          </div>
        </fieldset>

        <fieldset class="dsp-set">
          <legend>Acesso</legend>
          <div class="dsp-radios">
            <label><input type="radio" name="dsp-access" value="PUBLIC" ${f.access === "PUBLIC" ? "checked" : ""}> Público com link</label>
            <label><input type="radio" name="dsp-access" value="PASSWORD" ${f.access === "PASSWORD" ? "checked" : ""}> Protegido por senha adicional</label>
            <label class="off"><input type="radio" disabled> Somente usuários convidados <small>— não disponível: o sistema não tem cadastro de convidado externo.</small></label>
          </div>
          ${f.access === "PASSWORD" ? `<label class="dsp-field"><span>Senha do link (mínimo ${MIN_SHARE_PASSWORD_LENGTH} caracteres)</span><input type="password" id="dsp-password" autocomplete="new-password" value="${this.esc(f.password)}"></label>` : ""}
        </fieldset>

        <footer class="dsp-foot">
          <button type="button" class="dsp-btn ghost" data-close>Cancelar</button>
          <button type="submit" class="dsp-btn primary" ${this.busy || needsProject ? "disabled" : ""}>${this.busy ? "Gerando…" : "Gerar link"}</button>
        </footer>
      </form>`;
  }

  private stateBadge(l: DashboardShareLinkView): string {
    return `<span class="dsp-badge st-${l.state.toLowerCase()}">${this.esc(l.stateLabel)}</span>`;
  }

  private renderCreated(l: DashboardShareLinkView): string {
    const url = this.fullUrl(l.path);
    return `
      <div class="dsp-created">
        <h3>Link gerado ${this.stateBadge(l)}</h3>
        <div class="dsp-linkbox">
          <input id="dsp-link-value" readonly value="${this.esc(url)}" aria-label="Link compartilhado">
          <button type="button" class="dsp-btn sm" data-copy="${this.esc(url)}">Copiar</button>
          <a class="dsp-btn sm ghost" href="${this.esc(url)}" target="_blank" rel="noopener noreferrer">Abrir</a>
          ${l.canRevoke ? `<button type="button" class="dsp-btn sm danger" data-revoke="${this.esc(l.id)}">Revogar</button>` : ""}
        </div>
        ${this.revoking === l.id ? this.renderRevokeForm(l.id) : ""}
        <dl class="dsp-facts">
          <dt>Validade</dt><dd>${l.expiresAt ? `até ${this.when(l.expiresAt)}` : "Sem expiração"}</dd>
          <dt>Escopo</dt><dd>${this.scopeText(l)}</dd>
          <dt>Atualização</dt><dd>${this.refreshText(l)}</dd>
          <dt>Acesso</dt><dd>${l.access === "PASSWORD" ? "Protegido por senha" : "Público com link"}</dd>
        </dl>
        <p class="dsp-note">Quem tiver este link vê o Painel em modo somente leitura, sem acesso ao restante do INSPECT IA. Revogue quando não for mais necessário.</p>
        <footer class="dsp-foot">
          <button type="button" class="dsp-btn ghost" id="dsp-another">Gerar outro link</button>
          <button type="button" class="dsp-btn primary" data-close>Concluir</button>
        </footer>
      </div>`;
  }

  private scopeText(l: DashboardShareLinkView): string {
    const s = l.scope;
    return `${this.esc(s.mode === "FIXED" ? "Fixo" : "Dinâmico")} · ${this.esc(s.project)} · ${this.esc(s.unitArea)} · ${this.esc(s.inspector)} · ${this.esc(s.period)}${s.showInspectors ? " · com ranking por Inspetor" : ""}`;
  }

  private refreshText(l: DashboardShareLinkView): string {
    return l.refreshMode === "REALTIME" ? "Tempo real (15 s)" : l.refreshMode === "INTERVAL" ? `A cada ${l.refreshIntervalSeconds} s` : "Manual";
  }

  private renderRevokeForm(id: string): string {
    return `<div class="dsp-inline-form">
      <label class="dsp-field wide"><span>Motivo da revogação (opcional)</span><input id="dsp-revoke-reason" maxlength="300"></label>
      <div class="dsp-inline"><button type="button" class="dsp-btn sm ghost" id="dsp-revoke-cancel">Cancelar</button><button type="button" class="dsp-btn sm danger" data-revoke-confirm="${this.esc(id)}">Confirmar revogação</button></div>
      <p class="dsp-note">A revogação é definitiva: o link para de abrir na hora e não pode ser reativado.</p>
    </div>`;
  }

  private renderRenewForm(id: string): string {
    return `<div class="dsp-inline-form">
      <label class="dsp-field"><span>Nova validade</span><select id="dsp-renew-validity">${(Object.keys(SHARE_VALIDITY_LABELS) as ShareValidity[]).map(v => `<option value="${v}" ${v === "7D" ? "selected" : ""}>${SHARE_VALIDITY_LABELS[v]}</option>`).join("")}</select></label>
      <label class="dsp-field"><span>Expira em (se personalizada)</span><input type="datetime-local" id="dsp-renew-custom"></label>
      <label class="dsp-check"><input type="checkbox" id="dsp-renew-rotate"> Gerar endereço novo <small>(o link atual para de abrir)</small></label>
      <div class="dsp-inline"><button type="button" class="dsp-btn sm ghost" id="dsp-renew-cancel">Cancelar</button><button type="button" class="dsp-btn sm primary" data-renew-confirm="${this.esc(id)}">Renovar</button></div>
    </div>`;
  }

  private renderList(): string {
    if (!this.listLoaded) return this.busy ? `<p class="dsp-note">Carregando links…</p>` : `<p class="dsp-note">Não foi possível carregar os links.</p>`;
    if (this.links.length === 0) return `<div class="dsp-empty">Nenhum link gerado ainda.</div>`;
    return `<ul class="dsp-list">${this.links.map(l => {
      const url = this.fullUrl(l.path);
      return `<li class="dsp-item">
        <div class="dsp-item-head">
          <div><strong>${this.esc(l.name)}</strong> ${this.stateBadge(l)} ${l.access === "PASSWORD" ? `<span class="dsp-badge">SENHA</span>` : ""}</div>
          <div class="dsp-inline">
            ${l.state !== "REVOKED" ? `<button type="button" class="dsp-btn sm" data-copy="${this.esc(url)}">Copiar</button><a class="dsp-btn sm ghost" href="${this.esc(url)}" target="_blank" rel="noopener noreferrer">Abrir</a>` : ""}
            ${l.canEdit ? `<button type="button" class="dsp-btn sm ghost" data-renew="${this.esc(l.id)}">Renovar</button>` : ""}
            ${l.canRevoke ? `<button type="button" class="dsp-btn sm danger" data-revoke="${this.esc(l.id)}">Revogar</button>` : ""}
            <button type="button" class="dsp-btn sm ghost" data-history="${this.esc(l.id)}">Histórico</button>
          </div>
        </div>
        ${l.description ? `<p class="dsp-desc">${this.esc(l.description)}</p>` : ""}
        <dl class="dsp-facts">
          <dt>Validade</dt><dd>${l.state === "REVOKED" ? `Revogado em ${this.when(l.revokedAt)}${l.revokeReason ? ` — ${this.esc(l.revokeReason)}` : ""}` : l.expiresAt ? `${l.state === "EXPIRED" ? "Expirou em" : "Até"} ${this.when(l.expiresAt)}` : "Sem expiração"}</dd>
          <dt>Último acesso</dt><dd>${this.when(l.lastAccessAt)} · ${l.accessCount} abertura(s)</dd>
          <dt>Escopo</dt><dd>${this.scopeText(l)}</dd>
          <dt>Atualização</dt><dd>${this.refreshText(l)}</dd>
          <dt>Criado</dt><dd>${this.when(l.createdAt)} por ${this.esc(l.createdByName || l.createdBy)}</dd>
        </dl>
        ${this.renewing === l.id ? this.renderRenewForm(l.id) : ""}
        ${this.revoking === l.id ? this.renderRevokeForm(l.id) : ""}
        ${this.history?.id === l.id ? `<ul class="dsp-events">${this.history.events.length === 0 ? `<li>Sem eventos.</li>` : this.history.events.map(e => `<li><span>${this.when(e.at)}</span> ${this.esc(EVENT_LABELS[e.event] || e.event)} — ${this.esc(e.userId === "public" ? "acesso pelo link" : (e.userName || e.userId))}</li>`).join("")}</ul>` : ""}
      </li>`;
    }).join("")}</ul>`;
  }

  // ─────────────────────────────────────────────────────────── eventos

  private bind(): void {
    this.querySelectorAll<HTMLElement>("[data-close]").forEach(el => el.addEventListener("click", () => this.close()));
    this.querySelectorAll<HTMLElement>("[data-tab]").forEach(el => el.addEventListener("click", () => {
      this.tab = el.dataset["tab"] === "list" ? "list" : "new";
      this.error = ""; this.notice = "";
      this.render();
      if (this.tab === "list" && !this.listLoaded) void this.loadLinks();
    }));
    this.querySelectorAll<HTMLElement>("[data-copy]").forEach(el => el.addEventListener("click", () => void this.copy(el.dataset["copy"] || "")));
    this.querySelectorAll<HTMLElement>("[data-revoke]").forEach(el => el.addEventListener("click", () => { this.revoking = el.dataset["revoke"] || null; this.renewing = null; this.render(); }));
    this.querySelector("#dsp-revoke-cancel")?.addEventListener("click", () => { this.revoking = null; this.render(); });
    this.querySelectorAll<HTMLElement>("[data-revoke-confirm]").forEach(el => el.addEventListener("click", () =>
      void this.revoke(el.dataset["revokeConfirm"] || "", this.querySelector<HTMLInputElement>("#dsp-revoke-reason")?.value || "")));
    this.querySelectorAll<HTMLElement>("[data-renew]").forEach(el => el.addEventListener("click", () => { this.renewing = el.dataset["renew"] || null; this.revoking = null; this.render(); }));
    this.querySelector("#dsp-renew-cancel")?.addEventListener("click", () => { this.renewing = null; this.render(); });
    this.querySelectorAll<HTMLElement>("[data-renew-confirm]").forEach(el => el.addEventListener("click", () => void this.renew(
      el.dataset["renewConfirm"] || "",
      (this.querySelector<HTMLSelectElement>("#dsp-renew-validity")?.value || "7D") as ShareValidity,
      this.querySelector<HTMLInputElement>("#dsp-renew-custom")?.value || "",
      Boolean(this.querySelector<HTMLInputElement>("#dsp-renew-rotate")?.checked))));
    this.querySelectorAll<HTMLElement>("[data-history]").forEach(el => el.addEventListener("click", () => void this.showHistory(el.dataset["history"] || "")));
    this.querySelector("#dsp-another")?.addEventListener("click", () => { this.created = null; this.form = this.formFromFilters(this.init.filters); this.render(); });

    const form = this.querySelector<HTMLFormElement>("#dsp-form");
    if (!form) return;
    const f = this.form;
    const val = (id: string) => (this.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value ?? "";
    // Campos de texto: atualizam o estado sem redesenhar (não perdem o foco).
    const text: Array<[string, keyof FormState]> = [["dsp-name", "name"], ["dsp-desc", "description"], ["dsp-custom", "customExpiresAt"], ["dsp-password", "password"], ["dsp-from", "from"], ["dsp-to", "to"]];
    for (const [id, key] of text) this.querySelector(`#${id}`)?.addEventListener("input", () => { (f as any)[key] = val(id); });
    // Escolhas que mudam o formulário: redesenham.
    this.querySelector("#dsp-validity")?.addEventListener("change", () => { f.validity = val("dsp-validity") as ShareValidity; this.render(); });
    this.querySelector("#dsp-project")?.addEventListener("change", () => {
      f.projectId = val("dsp-project");
      if (f.unitAreaId && !this.init.unitAreas.some(u => u.id === f.unitAreaId && (!f.projectId || u.projectId === f.projectId))) f.unitAreaId = "";
      this.render();
    });
    this.querySelector("#dsp-unit")?.addEventListener("change", () => { f.unitAreaId = val("dsp-unit"); });
    this.querySelector("#dsp-inspector")?.addEventListener("change", () => { f.inspectorProfessionalId = val("dsp-inspector"); });
    this.querySelector("#dsp-interval")?.addEventListener("change", () => { f.refreshIntervalSeconds = Number(val("dsp-interval")); });
    this.querySelector("#dsp-show-inspectors")?.addEventListener("change", e => { f.showInspectors = (e.target as HTMLInputElement).checked; });
    this.querySelectorAll<HTMLInputElement>("input[name=dsp-mode]").forEach(r => r.addEventListener("change", () => { f.mode = r.value as ShareScopeMode; }));
    this.querySelectorAll<HTMLInputElement>("input[name=dsp-refresh]").forEach(r => r.addEventListener("change", () => { f.refreshMode = r.value as ShareRefreshMode; this.render(); }));
    this.querySelectorAll<HTMLInputElement>("input[name=dsp-access]").forEach(r => r.addEventListener("change", () => { f.access = r.value as ShareAccess; this.render(); }));
    this.querySelector("#dsp-use-filters")?.addEventListener("click", () => {
      const fresh = this.formFromFilters(this.init.filters);
      Object.assign(f, { projectId: fresh.projectId, unitAreaId: fresh.unitAreaId, inspectorProfessionalId: fresh.inspectorProfessionalId, from: fresh.from, to: fresh.to });
      this.render();
    });
    this.querySelector("#dsp-all")?.addEventListener("click", () => { Object.assign(f, { projectId: "", unitAreaId: "", inspectorProfessionalId: "", from: "", to: "" }); this.render(); });
    form.addEventListener("submit", e => { e.preventDefault(); void this.generate(); });
    (this.querySelector("#dsp-name") as HTMLInputElement | null)?.focus();
  }
}

const STYLES = `
  dashboard-share-panel { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .dsp-backdrop { position: absolute; inset: 0; background: rgba(5, 8, 11, 0.72); }
  .dsp-modal { position: relative; width: min(880px, 100%); max-height: calc(100vh - 32px); display: flex; flex-direction: column; background: var(--color-surface-primary, #12161A); color: var(--color-text-primary, #E7EAED); border: 1px solid rgba(255,255,255,0.10); border-radius: 12px; box-shadow: 0 24px 60px rgba(0,0,0,0.5); font-size: 0.86rem; }
  .dsp-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 18px; border-bottom: 1px solid rgba(255,255,255,0.08); }
  .dsp-head h2 { margin: 0; font-size: 0.95rem; letter-spacing: 0.05em; text-transform: uppercase; }
  .dsp-icon { background: none; border: 0; color: var(--ia-fg-soft, #9BA4AE); font-size: 1rem; cursor: pointer; padding: 4px 8px; border-radius: 6px; }
  .dsp-icon:hover { background: rgba(255,255,255,0.06); color: var(--ia-fg, #E7EAED); }
  .dsp-tabs { display: flex; gap: 4px; padding: 10px 18px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
  .dsp-tabs button { background: none; border: 0; border-bottom: 2px solid transparent; color: var(--ia-fg-soft, #9BA4AE); padding: 8px 12px; cursor: pointer; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 0.74rem; }
  .dsp-tabs button.on { color: var(--ia-fg, #E7EAED); border-bottom-color: var(--color-primary, #C9A227); }
  .dsp-body { padding: 16px 18px; overflow-y: auto; }
  .dsp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
  .dsp-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .dsp-field.wide { grid-column: 1 / -1; }
  .dsp-field > span { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
  .dsp-field input, .dsp-field select, .dsp-field textarea, .dsp-radios select, .dsp-period input { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.12)); color: var(--ia-fg, #DCE2E8); font: inherit; }
  .dsp-radios select { width: auto; padding: 4px 8px; }
  .dsp-period { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .dsp-period input { flex: 1 1 14em; min-width: 14em; }
  .dsp-set { border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 12px 14px; margin: 14px 0 0; display: flex; flex-direction: column; gap: 10px; }
  .dsp-set legend { padding: 0 6px; font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg, #C8D2DC); font-weight: 700; }
  .dsp-radios { display: flex; flex-direction: column; gap: 8px; }
  .dsp-radios label, .dsp-check { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; line-height: 1.4; }
  .dsp-radios label.off { color: var(--ia-fg-muted, #8A939D); }
  .dsp-check small, .dsp-radios small { color: var(--ia-fg-muted, #8A939D); }
  .dsp-note { margin: 0; color: var(--ia-fg-muted, #8A939D); font-size: 0.78rem; line-height: 1.45; }
  .dsp-note.dsp-need { color: var(--ia-warning-fg, #E7C28A); font-weight: 600; }
  .dsp-inline { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .dsp-foot { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; margin-top: 16px; }
  .dsp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 14px; border-radius: 8px; font-size: 0.76rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; border: 1px solid var(--ia-line, rgba(255,255,255,0.12)); background: var(--ia-bg-panel, #1D2329); color: var(--ia-fg, #E7EAED); text-decoration: none; white-space: nowrap; }
  .dsp-btn.sm { padding: 6px 10px; font-size: 0.7rem; }
  .dsp-btn.primary { background: var(--ia-bg-raised, #2B3A47); border-color: var(--ia-line, #3A4C5C); }
  .dsp-btn.primary:hover:not(:disabled) { background: var(--ia-bg-raised, #334554); }
  .dsp-btn.ghost { background: var(--ia-bg-panel, #171C21); color: var(--ia-fg-soft, #C3CAD1); }
  .dsp-btn.danger { background: rgba(167, 111, 111, 0.16); border-color: rgba(200, 120, 120, 0.4); color: var(--ia-fg, #F0C9C9); }
  .dsp-btn:disabled { opacity: .55; cursor: default; }
  .dsp-alert { padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 0.8rem; }
  .dsp-alert.err { background: rgba(167, 111, 111, 0.14); border: 1px solid rgba(200, 120, 120, 0.4); color: var(--ia-fg, #F0C9C9); }
  .dsp-alert.ok { background: rgba(110, 158, 126, 0.14); border: 1px solid rgba(110, 158, 126, 0.4); color: var(--ia-fg, #CFE6D6); }
  .dsp-created h3 { margin: 0 0 12px; font-size: 0.9rem; display: flex; gap: 8px; align-items: center; }
  .dsp-linkbox { display: flex; gap: 8px; flex-wrap: wrap; }
  .dsp-linkbox input { flex: 1 1 320px; min-width: 0; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.12)); color: var(--ia-fg, #DCE2E8); font-family: ui-monospace, Consolas, monospace; font-size: 0.78rem; }
  .dsp-facts { display: grid; grid-template-columns: max-content 1fr; gap: 4px 14px; margin: 12px 0; font-size: 0.8rem; }
  .dsp-facts dt { color: var(--ia-fg-muted, #8A939D); }
  .dsp-facts dd { margin: 0; color: var(--ia-fg, #D6DCE2); overflow-wrap: anywhere; }
  .dsp-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.06em; background: rgba(255,255,255,0.06); color: var(--ia-fg, #C8D2DC); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); }
  .dsp-badge.st-active { background: rgba(110, 158, 126, 0.16); color: var(--ia-fg, #CFE6D6); border-color: rgba(110, 158, 126, 0.4); }
  .dsp-badge.st-expired { background: rgba(178, 148, 85, 0.16); color: var(--ia-warning-fg, #EADBB8); border-color: rgba(178, 148, 85, 0.4); }
  .dsp-badge.st-revoked { background: rgba(167, 111, 111, 0.16); color: var(--ia-fg, #F0C9C9); border-color: rgba(200, 120, 120, 0.4); }
  .dsp-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
  .dsp-item { border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 12px 14px; }
  .dsp-item-head { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; align-items: center; }
  .dsp-desc { margin: 6px 0 0; color: var(--ia-fg-soft, #9BA4AE); }
  .dsp-inline-form { border-top: 1px dashed rgba(255,255,255,0.10); margin-top: 10px; padding-top: 10px; display: flex; flex-direction: column; gap: 8px; }
  .dsp-events { list-style: none; margin: 8px 0 0; padding: 8px 0 0; border-top: 1px dashed var(--ia-line, rgba(255,255,255,0.10)); display: flex; flex-direction: column; gap: 4px; font-size: 0.78rem; color: var(--ia-fg-soft, #C3CAD1); }
  .dsp-events span { color: var(--ia-fg-muted, #8A939D); font-variant-numeric: tabular-nums; margin-right: 6px; }
  .dsp-empty { padding: 24px; text-align: center; color: var(--ia-fg-muted, #8A939D); border: 1px dashed var(--ia-line, rgba(255,255,255,0.10)); border-radius: 10px; }
  @media (max-width: 600px) {
    dashboard-share-panel { padding: 0; align-items: stretch; }
    .dsp-modal { max-height: 100vh; border-radius: 0; }
    .dsp-facts { grid-template-columns: 1fr; }
    .dsp-period input { min-width: 0; flex-basis: 100%; }
  }
`;

if (!customElements.get("dashboard-share-panel")) {
  customElements.define("dashboard-share-panel", DashboardSharePanel);
}
